import { format } from "date-fns";

import type { Schemas } from "@shared/api/types";

import { CHARGE_TYPE, HOURS_PER_DAY, TYPE_OF_CHARGE } from "../constants";
import { automaticProductiveHours, isZeroHourActivity } from "./hourRules";
import type { AutomaticChargeValues } from "../schema/automaticChargeSchema";
import type { ChargedHourValues } from "../schema/chargedHourSchema";
import type { MissingHourValues } from "../schema/missingHourSchema";
import type { RegisterHourDescriptiveValues } from "../schema/registerHourDescriptiveSchema";

// Builders puros del payload de imputación.

type Activity = Schemas["ActivityDTO"];
type MissingHoursWeeklySummary = Schemas["MissingHoursWeeklySummaryDTO"];

/**
 * Campos de `MissingHoursWeeklySummaryDTO` que este módulo asume presentes: el
 * contrato los declara opcionales, pero los call sites (MissingHourDialog,
 * MissingHourSummary) siempre los pueblan. Comportamiento preexistente, no se
 * agregan guards nuevos.
 */
type MissingHourSource = Required<Pick<MissingHoursWeeklySummary, "projectId" | "days">>;
type WeekChargeSource = Required<Pick<MissingHoursWeeklySummary, "projectId" | "missingHours" | "from">>;

const today = () => new Date().toISOString().substring(0, 10);

/** Payload para completar una hora faltante (MissingHourDialog). */
export const buildMissingHourEntry = (
  values: MissingHourValues,
  {
    missingHour,
    activity,
    issueId,
  }: { missingHour: MissingHourSource; activity: Activity | null | undefined; issueId: number }
) => {
  const [day] = missingHour.days.filter(
    (item) => item.date === format(values.spentOn, "yyyy-MM-dd")
  );
  return {
    ...values,
    projectId: missingHour.projectId,
    issueId,
    // `spentOn` se elige entre los `days` de la misma fila, asi que siempre hay match.
    hours: isZeroHourActivity(activity) ? 0 : day!.missingHours,
  };
};

/** Payload de carga automática semanal/mensual (RegisterHourAutomaticDialog). */
export const buildAutomaticChargeEntry = (
  values: AutomaticChargeValues,
  { issueId, weekDaysOfMonth }: { issueId: number; weekDaysOfMonth: number }
) => ({
  ...values,
  hourProductive: automaticProductiveHours(values.typeOfCharge, values.hoursPerDay, weekDaysOfMonth),
  issueId,
  type: CHARGE_TYPE.AUTOMATIC,
  localDate: today(),
});

/** Payload de carga general (ChargedHourDialog). Normaliza la fecha a yyyy-MM-dd. */
export const buildChargedHourEntry = (
  values: Partial<ChargedHourValues> & Pick<ChargedHourValues, "spentOn" | "hours">,
  { issueId, hoursVisible }: { issueId: number; hoursVisible: boolean }
) => ({
  ...values,
  issueId,
  spentOn: values.spentOn.toISOString().substring(0, 10),
  hours: hoursVisible ? values.hours : 0,
});

/** Payload de carga descriptiva manual (RegisterHourDescriptive). */
export const buildDescriptiveEntry = (
  values: Partial<RegisterHourDescriptiveValues>,
  { issueId }: { issueId: number }
) => ({
  ...values,
  issueId,
  type: CHARGE_TYPE.MANUAL,
  localDate: today(),
});

/** Payload de carga semanal automática desde el resumen (MissingHourSummary). */
export const buildWeekChargeEntry = (missingHour: WeekChargeSource, issueId: number) => ({
  projectId: missingHour.projectId,
  hourProductive: missingHour.missingHours,
  issueId,
  type: CHARGE_TYPE.AUTOMATIC,
  typeOfCharge: TYPE_OF_CHARGE.WEEK,
  hoursPerDay: HOURS_PER_DAY,
  localDate: new Date(missingHour.from).toISOString().substring(0, 10),
});
